import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import { useDispatch } from 'react-redux';
import {
  ActivityIndicator,
  Image,
  Platform,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import AnimatedLottieView from 'lottie-react-native';
import { BottomSheetModal, BottomSheetTextInput } from '@gorhom/bottom-sheet';
import PhoneInput, {
  ICountry,
  IPhoneInputRef,
} from 'rn-international-phone-number';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import { IconButton, Menu } from 'react-native-paper';
import { useTheme, GTFonts, LatoFonts } from '../../theme';
import {
  borderWidth,
  formatNumericStringWithCommas,
  formatWithCommas,
  formatNumberDecimal,
  fs,
  forceHttps,
  iconSize,
  normalizeSafaricomPhone,
  sanitizeNumericInput,
  replaceCurrencyString,
  s,
  safaricomNumberValidation,
  v,
} from '../../utils/libs';
import { buildE164FromInput, PREFERRED_COUNTRIES } from '../../utils/phone';
import Button from '../../components/Reusables/Button';
import SafeAreaWrapper from '../../components/Reusables/SafeAreaWrapper';
import CustomBottomSheet from '../../components/Reusables/bottomSheet/CustomBottomSheet';
import NameModal from '../../components/Modals/NameModal';
import { AppSnackbar } from '../../services/AppSnackbar';
import { AppAlert } from '../../services/AppAlert';
import { ModalContext } from '../../Context/modalContext';
import {
  useDemoCart,
  useDemoCheckout,
  type DemoCartItemData,
} from '../../hooks/demo';
import {
  CheckoutPaymentMethod,
  MpesaPaymentMode,
  setCheckoutPaymentIntent,
  setCheckoutPaymentSelection,
} from '../../redux/checkoutSlice';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import DemoAttentionPulse from './components/DemoAttentionPulse';
import Typography from '../../components/Reusables/Typography';
import QuantityControls from '../../components/Reusables/QuantityControls';
import { shouldUseWebLayout } from '../../utils/layoutUtils';

const isWeb = Platform.OS === 'web';

interface DemoCartScreenProps {
  navigation: any;
}

const DemoCartScreen: React.FC<DemoCartScreenProps> = ({ navigation }) => {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWebLayout(width, height);
  const { visibleName, setVisibleName, setInput } = useContext(ModalContext);
  const styles = useMemo(
    () => createStyles(theme, constrainWidth),
    [theme, constrainWidth],
  );
  const {
    cartItems,
    isLoading,
    cartTotal,
    updateCartItemQuantity,
    updateCartItemMass,
    removeFromCart,
    clearCart,
  } = useDemoCart();
  const { createOrder, isCreatingOrder } = useDemoCheckout();
  const { countryCode, iCountry, currency } = useDetectedCountry();
  const phoneInputRef = useRef<IPhoneInputRef>(null);

  const checkoutPaymentSheetRef = useRef<BottomSheetModal>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<CheckoutPaymentMethod>('cash');
  const [cashTendered, setCashTendered] = useState('');
  const [mpesaPhoneInput, setMpesaPhoneInput] = useState('');
  const [mpesaSheetMode, setMpesaSheetMode] =
    useState<MpesaPaymentMode>('PAID_TO_TILL');
  const [isWaitingToProceed, setIsWaitingToProceed] = useState(false);
  const [phoneInputValue, setPhoneInputValue] = useState<string>('');
  const [phoneSelectedCountry, setPhoneSelectedCountry] = useState<
    ICountry | undefined
  >(undefined);
  const [massInputs, setMassInputs] = useState<Record<string, string>>({});
  const [massFocusedItemId, setMassFocusedItemId] = useState<string | null>(
    null,
  );

  const currencyTextLower = useMemo(
    () => replaceCurrencyString('ksh', currency),
    [currency],
  );
  const currencyTextCapital = useMemo(
    () => replaceCurrencyString('KSH', currency),
    [currency],
  );

  useEffect(() => {
    setMassInputs(prev => {
      const next: Record<string, string> = { ...prev };

      // Sync current unit items (but don't stomp the actively edited one)
      for (const item of cartItems) {
        if (item.price_type !== 'unit') continue;
        if (massFocusedItemId === item._id) continue;
        next[item._id] = item.mass > 0 ? String(item.mass) : '';
      }

      // Cleanup removed unit items
      for (const key of Object.keys(next)) {
        const stillExists = cartItems.some(
          i => i._id === key && i.price_type === 'unit',
        );
        if (!stillExists) delete next[key];
      }

      return next;
    });
  }, [cartItems, massFocusedItemId]);

  useEffect(() => {
    if (!phoneSelectedCountry && iCountry) {
      setPhoneSelectedCountry(iCountry);
    }
  }, [iCountry, phoneSelectedCountry]);

  const normalizedSheetPhone = useMemo(
    () => normalizeSafaricomPhone(mpesaPhoneInput),
    [mpesaPhoneInput],
  );

  const manualAmountValue = useMemo(
    () => Number(sanitizeNumericInput(cashTendered || '0', { isUnit: true }) || 0),
    [cashTendered],
  );

  const isProceedDisabled =
    selectedPaymentMethod === 'mpesa' && mpesaSheetMode === 'STK'
      ? true
      : manualAmountValue < cartTotal;

  const handleSheetChanges = useCallback((index: number) => {
    if (index === -1) {
      setIsWaitingToProceed(false);
    }
  }, []);

  const handleCheckoutButtonPress = useCallback(() => {
    checkoutPaymentSheetRef.current?.present();
  }, []);

  const handleSelectCash = useCallback(() => {
    setSelectedPaymentMethod('cash');
    dispatch(
      setCheckoutPaymentSelection({
        cash: true,
        mpesa: false,
        payLater: false,
      }),
    );
    dispatch(
      setCheckoutPaymentIntent({
        method: 'cash',
        cashAmount: cashTendered,
      }),
    );
  }, [cashTendered, dispatch]);

  const handleSelectMpesa = useCallback(() => {
    const nextMode: MpesaPaymentMode = 'PAID_TO_TILL';
    setSelectedPaymentMethod('mpesa');
    setMpesaSheetMode(nextMode);
    dispatch(
      setCheckoutPaymentSelection({
        cash: false,
        mpesa: true,
        payLater: false,
      }),
    );
    dispatch(
      setCheckoutPaymentIntent({
        method: 'mpesa',
        cashAmount: nextMode === 'PAID_TO_TILL' ? cashTendered : '',
        mpesa: {
          phone: normalizeSafaricomPhone(mpesaPhoneInput),
          mode: nextMode,
        },
      }),
    );
  }, [cashTendered, dispatch, mpesaPhoneInput]);

  const handleCashAmountChange = useCallback(
    (value: string) => {
      const parsed = sanitizeNumericInput(value, { isUnit: true });
      const parsedNumber = Number(parsed || 0);
      setCashTendered(parsedNumber <= 0 ? '' : parsed);
      dispatch(
        setCheckoutPaymentIntent({
          method: 'cash',
          cashAmount: parsedNumber <= 0 ? '' : parsed,
        }),
      );
      dispatch(
        setCheckoutPaymentSelection({
          cash: true,
          mpesa: false,
          payLater: false,
        }),
      );
    },
    [dispatch],
  );

  const handleManualMpesaAmountChange = useCallback(
    (value: string) => {
      const parsed = sanitizeNumericInput(value, { isUnit: true });
      const parsedNumber = Number(parsed || 0);
      setCashTendered(parsedNumber <= 0 ? '' : parsed);
      dispatch(
        setCheckoutPaymentIntent({
          method: 'mpesa',
          cashAmount: parsedNumber <= 0 ? '' : parsed,
          mpesa: { mode: 'PAID_TO_TILL' },
        }),
      );
      dispatch(
        setCheckoutPaymentSelection({
          cash: false,
          mpesa: true,
          payLater: false,
        }),
      );
    },
    [dispatch],
  );

  const handleMpesaCountryChange = useCallback(
    (country: ICountry) => {
      setPhoneSelectedCountry(country);
      const e164 = buildE164FromInput(phoneInputValue, country);
      setMpesaPhoneInput(e164);
      dispatch(
        setCheckoutPaymentIntent({
          method: 'mpesa',
          mpesa: {
            phone: normalizeSafaricomPhone(e164),
          },
        }),
      );
    },
    [dispatch, phoneInputValue],
  );

  const handleMpesaPhoneChange = useCallback(
    (value: string) => {
      const startsWithZero = value.startsWith('0');
      if (startsWithZero) {
        value = value.slice(1);
      }
      const code = String(
        (phoneSelectedCountry as any)?.callingCode || '',
      ).replace(/^\+/, '');
      const nationalValue =
        code && value.startsWith(code) ? value.slice(code.length) : value;
      setPhoneInputValue(nationalValue);
      const e164 = buildE164FromInput(nationalValue, phoneSelectedCountry);
      setMpesaPhoneInput(e164);
      dispatch(
        setCheckoutPaymentIntent({
          method: 'mpesa',
          mpesa: {
            phone: normalizeSafaricomPhone(e164),
          },
        }),
      );
    },
    [dispatch, phoneSelectedCountry],
  );

  const handleMpesaModeChange = useCallback(
    (mode: MpesaPaymentMode) => {
      setMpesaSheetMode(mode);
      dispatch(
        setCheckoutPaymentIntent({
          method: 'mpesa',
          mpesa: { mode },
        }),
      );
      dispatch(
        setCheckoutPaymentSelection({
          cash: false,
          mpesa: true,
          payLater: false,
        }),
      );
    },
    [dispatch],
  );

  const createDemoOrder = useCallback(async () => {
    if (cartItems.length === 0) {
      AppSnackbar.show('No items in basket to checkout', 'warning', {
        durationMs: 3000,
      });
      return null;
    }

    const result = await createOrder({
      cartItems: cartItems.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        mass: item.mass,
        unit_price: item.unit_price,
        price_type: item.price_type,
        name: item.name,
        unit_of_mass: item.unit_of_mass,
        category: item.category,
        image: item.image,
      })),
      customerName: 'Walk in customer',
      businessName: 'demo location',
      customerPhone:
        selectedPaymentMethod === 'mpesa'
          ? normalizeSafaricomPhone(mpesaPhoneInput)
          : '',
      paymentMethod: selectedPaymentMethod === 'cash' ? 'Cash' : 'Mpesa',
    });

    if (!result) {
      AppSnackbar.show('Failed to create demo order. Try again.', 'error', {
        durationMs: 7500,
      });
      return null;
    }

    return result;
  }, [cartItems, createOrder, mpesaPhoneInput, selectedPaymentMethod]);

  const completeDemoCheckout = useCallback(
    async (methodLabel: string) => {
      setIsWaitingToProceed(true);
      const created = await createDemoOrder();
      setIsWaitingToProceed(false);
      if (!created) return;
      if (selectedPaymentMethod === 'cash') {
        dispatch(
          setCheckoutPaymentIntent({
            method: 'cash',
            cashAmount: cashTendered,
          }),
        );
        dispatch(
          setCheckoutPaymentSelection({
            cash: true,
            mpesa: false,
            payLater: false,
          }),
        );
      } else {
        dispatch(
          setCheckoutPaymentIntent({
            method: 'mpesa',
            cashAmount: mpesaSheetMode === 'PAID_TO_TILL' ? cashTendered : '',
            mpesa: {
              phone: normalizeSafaricomPhone(mpesaPhoneInput),
              mode: mpesaSheetMode,
            },
          }),
        );
        dispatch(
          setCheckoutPaymentSelection({
            cash: false,
            mpesa: true,
            payLater: false,
          }),
        );
      }
      checkoutPaymentSheetRef.current?.dismiss();
      console.log(
        '[DemoCartScreen] Demo checkout payload:',
        created.checkoutPayload,
      );
      navigation.replace('DemoCheckout', created.checkoutPayload);
    },
    [
      cashTendered,
      createDemoOrder,
      dispatch,
      mpesaPhoneInput,
      mpesaSheetMode,
      navigation,
      selectedPaymentMethod,
    ],
  );

  const handleProceedToCheckout = useCallback(async () => {
    if (isProceedDisabled || isCreatingOrder) return;
    const label = selectedPaymentMethod === 'cash' ? 'Cash' : 'Mpesa';
    await completeDemoCheckout(label);
  }, [
    completeDemoCheckout,
    isCreatingOrder,
    isProceedDisabled,
    selectedPaymentMethod,
  ]);

  const handleCartMpesaStkPush = useCallback(() => {
    if (!safaricomNumberValidation(normalizedSheetPhone) || isCreatingOrder)
      return;
    AppSnackbar.show(
      'STK Push is not available in demo mode. Create a BizPlus POS account to use this feature.',
      'info',
      { durationMs: 5000 },
    );
  }, [isCreatingOrder, normalizedSheetPhone]);

  const handleAdd = useCallback(
    async (item: DemoCartItemData) => {
      const maxAvailable = item.max_available ?? Infinity;

      if (item.price_type === 'unit') {
        const newMass = item.mass + 1;
        if (newMass > maxAvailable) {
          AppSnackbar.show(
            `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${item.unit_of_mass}`,
            'error',
            { durationMs: 7500 },
          );
          return;
        }
        await updateCartItemMass(item._id, newMass);
      } else {
        const newQuantity = item.quantity + 1;
        if (newQuantity > maxAvailable) {
          AppSnackbar.show(
            `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)}`,
            'error',
            { durationMs: 7500 },
          );
          return;
        }
        await updateCartItemQuantity(item._id, newQuantity);
      }
    },
    [updateCartItemMass, updateCartItemQuantity],
  );

  const handleSub = useCallback(
    async (item: DemoCartItemData) => {
      if (item.price_type === 'unit') {
        const nextMass = Math.max(0, item.mass - 1);
        // Block only if this would empty the cart
        if (nextMass <= 0 && cartItems.length <= 1) {
          AppSnackbar.show(
            'At least one item must remain in the demo basket',
            'warning',
            { durationMs: 7500 },
          );
          return;
        }
        await updateCartItemMass(item._id, nextMass);
      } else {
        const nextQuantity = Math.max(0, item.quantity - 1);
        // Block only if this would empty the cart
        if (nextQuantity <= 0 && cartItems.length <= 1) {
          AppSnackbar.show(
            'At least one item must remain in the demo basket',
            'warning',
            { durationMs: 7500 },
          );
          return;
        }
        await updateCartItemQuantity(item._id, nextQuantity);
      }
    },
    [cartItems.length, updateCartItemMass, updateCartItemQuantity],
  );

  const handleQuantityInputChange = useCallback(
    async (item: DemoCartItemData, input: string) => {
      const parsed = Number.parseInt(input.replace(/[^\d]/g, ''), 10);
      const nextQuantity = Number.isNaN(parsed) ? 0 : parsed;

      if (nextQuantity <= 0 && cartItems.length <= 1) {
        AppSnackbar.show(
          'At least one item must remain in the demo basket',
          'warning',
          { durationMs: 7500 },
        );
        return;
      }

      const maxAvailable = item.max_available ?? Infinity;
      if (nextQuantity > maxAvailable) {
        AppSnackbar.show(
          `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)}`,
          'error',
          { durationMs: 7500 },
        );
        await updateCartItemQuantity(item._id, maxAvailable);
        return;
      }

      await updateCartItemQuantity(item._id, nextQuantity);
    },
    [cartItems.length, updateCartItemQuantity],
  );

  const handleMassInputChange = useCallback(
    (item: DemoCartItemData, input: string) => {
      let sanitized = input.replace(',', '.').replace(/[^0-9.]/g, '');
      sanitized = sanitized.replace(/^0+(?=\d)/, '');
      if (sanitized.startsWith('.')) sanitized = `0${sanitized}`;

      if (sanitized.length === 0) {
        if (cartItems.length <= 1) {
          AppSnackbar.show(
            'At least one item must remain in the demo basket',
            'warning',
            { durationMs: 7500 },
          );
          setMassInputs(prev => ({
            ...prev,
            [item._id]: item.mass > 0 ? String(item.mass) : '',
          }));
          return;
        }

        setMassInputs(prev => ({ ...prev, [item._id]: '' }));
        updateCartItemMass(item._id, 0).catch(() => {});
        return;
      }

      if (!/^\d*\.?\d*$/.test(sanitized)) return;

      setMassInputs(prev => ({ ...prev, [item._id]: sanitized }));
      if (sanitized.endsWith('.')) return;

      const parsed = Number.parseFloat(sanitized);
      if (Number.isNaN(parsed)) return;
      if (parsed <= 0 && cartItems.length <= 1) {
        AppSnackbar.show(
          'At least one item must remain in the demo basket',
          'warning',
          { durationMs: 7500 },
        );
        setMassInputs(prev => ({
          ...prev,
          [item._id]: item.mass > 0 ? String(item.mass) : '',
        }));
        return;
      }

      const maxAvailable = item.max_available ?? Infinity;
      if (parsed > maxAvailable) {
        AppSnackbar.show(
          `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${item.unit_of_mass}`,
          'error',
          { durationMs: 7500 },
        );
        setMassInputs(prev => ({
          ...prev,
          [item._id]: String(maxAvailable),
        }));
        updateCartItemMass(item._id, maxAvailable).catch(() => {});
        return;
      }

      updateCartItemMass(item._id, parsed).catch(() => {});
    },
    [cartItems.length, updateCartItemMass],
  );

  const handleMassInputBlur = useCallback(
    (item: DemoCartItemData) => {
      setMassFocusedItemId(null);

      const current = massInputs[item._id] ?? '';
      if (!current) return;

      const maxAvailable = item.max_available ?? Infinity;

      if (current.endsWith('.')) {
        const trimmed = current.slice(0, -1);
        const parsed = Number.parseFloat(trimmed);
        if (Number.isNaN(parsed)) {
          if (cartItems.length <= 1) {
            AppSnackbar.show(
              'At least one item must remain in the demo basket',
              'warning',
              { durationMs: 7500 },
            );
            setMassInputs(prev => ({
              ...prev,
              [item._id]: item.mass > 0 ? String(item.mass) : '',
            }));
            return;
          }

          setMassInputs(prev => ({ ...prev, [item._id]: '' }));
          updateCartItemMass(item._id, 0).catch(() => {});
          return;
        }

        if (parsed > maxAvailable) {
          AppSnackbar.show(
            `Value is greater than available stock of ${formatNumberDecimal(maxAvailable)} ${item.unit_of_mass}`,
            'error',
            { durationMs: 7500 },
          );
          setMassInputs(prev => ({
            ...prev,
            [item._id]: String(maxAvailable),
          }));
          updateCartItemMass(item._id, maxAvailable).catch(() => {});
          return;
        }

        setMassInputs(prev => ({
          ...prev,
          [item._id]: parsed > 0 ? String(parsed) : '',
        }));
        if (parsed <= 0 && cartItems.length <= 1) {
          AppSnackbar.show(
            'At least one item must remain in the demo basket',
            'warning',
            { durationMs: 7500 },
          );
          setMassInputs(prev => ({
            ...prev,
            [item._id]: item.mass > 0 ? String(item.mass) : '',
          }));
          return;
        }
        updateCartItemMass(item._id, parsed).catch(() => {});
      }
    },
    [cartItems.length, massInputs, updateCartItemMass],
  );

  const handleRemove = useCallback(
    async (item: DemoCartItemData) => {
      if (cartItems.length <= 1) {
        AppSnackbar.show(
          'At least one item must remain in the demo basket',
          'warning',
          { durationMs: 7500 },
        );
        return;
      }
      await removeFromCart(item._id);
    },
    [cartItems.length, removeFromCart],
  );

  const handleCancel = useCallback(() => {
    if (cartItems.length === 0) return;
    AppAlert.alert(
      'Cancel Order ?',
      'Are you sure you want to clear this demo order?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          onPress: async () => {},
        },
      ],
    );
  }, [cartItems.length]);

  const handleDemoPayLaterSave = useCallback(() => {
    setInput('');
    setVisibleName(false);
    AppSnackbar.show('Pay Later is not supported in demo mode, please create a BizPlus POS account to use this feature.', 'info', {
      durationMs: 7500,
    });
  }, [setInput, setVisibleName]);

  const showName = useCallback(() => {
    setVisibleName(true);
  }, [setVisibleName]);

  const handleSaveOrder = useCallback(() => {
    const isLessthanZero = cartItems.find(item =>
      item.price_type === 'unit' ? item.mass <= 0 : item.quantity <= 0,
    );
    if (isLessthanZero) {
      AppSnackbar.show(
        `The quantity of ${isLessthanZero.name} is invalid, please check the order`,
        'error',
        { durationMs: 3000 },
      );
      return;
    }
    showName();
  }, [cartItems, showName]);

  const handlePayLater = useCallback(() => {
    handleSaveOrder();
  }, [handleSaveOrder]);

  return (
    <SafeAreaWrapper
      edges={['left', 'right']}
      backgroundColor={theme.colors.background}
      statusBarStyle="light-content">
      <View style={styles.container}>
        {isLoading ? (
          <View style={styles.loadingState}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : cartItems.length > 0 ? (
          <>
            <View style={styles.cartContainer}>
              <View style={styles.listHeaderRow}>
                <Typography style={styles.listHeaderText}>Item</Typography>
                <Typography style={styles.listHeaderText}>
                  Price/Quantity
                </Typography>
              </View>
              {cartItems.map(item => (
                <View key={item._id} style={styles.box}>
                  <View style={styles.cardheader}>
                    <View style={styles.leftSection}>
                      {item.image ? (
                        <Image
                          source={{ uri: forceHttps(item.image) }}
                          style={styles.itemImage}
                        />
                      ) : (
                        <View style={styles.emptyImagePlaceholder}>
                          <FontAwesome6
                            name="image"
                            size={iconSize(12)}
                            color={theme.colors.textSecondary}
                          />
                        </View>
                      )}
                      <View style={styles.textColumn}>
                        <Typography style={styles.nameText} numberOfLines={1}>
                          {item.name}
                        </Typography>
                        <Typography style={styles.categoryText}>
                          {item.category}
                        </Typography>
                      </View>
                    </View>
                    <View style={styles.rightSection}>
                      <TouchableOpacity
                        style={styles.priceRow}
                        onPress={() => {
                          AppSnackbar.show(
                            'Edit price not available in demo mode. Create a BizPlus POS account to use this feature.',
                            'info',
                            { durationMs: 5000 },
                          );
                          return;
                        }}>
                        <Typography style={styles.qty}>
                          {currencyTextLower}
                          {'\u00A0'}
                          {item.price_type === 'unit'
                            ? `${formatWithCommas(item.unit_price)}/${item.unit_of_mass}`
                            : formatWithCommas(item.unit_price)}
                        </Typography>
                        <TouchableOpacity
                          activeOpacity={1}
                          onPress={() => {
                            AppSnackbar.show(
                              'Edit price not available in demo mode. Create a BizPlus POS account to use this feature.',
                              'info',
                              { durationMs: 5000 },
                            );
                            return;
                          }}
                          style={styles.editAmountButton}>
                          <FontAwesome6
                            name="pen"
                            iconStyle="solid"
                            size={iconSize(12)}
                            color={theme.colors.primary}
                          />
                        </TouchableOpacity>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.trashButton}
                        onPress={() => handleRemove(item)}>
                        <FontAwesome6
                          name="trash"
                          iconStyle="solid"
                          size={iconSize(14)}
                          color={theme.colors.error}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>

                  <View style={styles.bottom}>
                    <Typography style={styles.subTotal}>
                      <Typography style={styles.subTotalPrefix}>
                        Sub-Total:{'\u00A0'}
                      </Typography>
                      {currencyTextLower}
                      {'\u00A0'}
                      {item.price_type === 'unit'
                        ? formatWithCommas(item.unit_price * item.mass)
                        : formatWithCommas(item.unit_price * item.quantity)}
                    </Typography>

                    <View style={styles.btnQuantity}>
                      <QuantityControls
                        priceType={item.price_type === 'unit' ? 'unit' : 'item'}
                        quantity={item.quantity}
                        mass={item.mass}
                        massText={massInputs[item._id]}
                        unitOfMass={item.unit_of_mass}
                        onAdd={() => handleAdd(item)}
                        onSubtract={() => handleSub(item)}
                        onQuantityChange={(input: string) =>
                          handleQuantityInputChange(item, input)
                        }
                        onMassChange={(input: string) =>
                          handleMassInputChange(item, input)
                        }
                        onMassFocus={() => setMassFocusedItemId(item._id)}
                        onMassBlur={() => handleMassInputBlur(item)}
                        useWebLayout={constrainWidth}
                      />
                    </View>
                  </View>
                </View>
              ))}
            </View>

            <CustomBottomSheet
              bottomSheetModalRef={checkoutPaymentSheetRef}
              snapPoints={['60%', '90%']}
              enableDynamicSizing={false}
              handleSheetChanges={handleSheetChanges}
              constrainWidth
              footer={
                selectedPaymentMethod === 'mpesa' &&
                mpesaSheetMode === 'STK' ? null : (
                  <View style={styles.sheetFooter}>
                    <DemoAttentionPulse
                      active={!isProceedDisabled && !isCreatingOrder}>
                      <Button
                        text={
                          selectedPaymentMethod === 'cash'
                            ? `Proceed with Cash - ${currencyTextCapital} ${formatWithCommas(cartTotal)}`
                            : `Proceed with Mpesa - ${currencyTextCapital} ${formatWithCommas(cartTotal)}`
                        }
                        disabled={isProceedDisabled || isCreatingOrder}
                        loading={isWaitingToProceed || isCreatingOrder}
                        height={v(46)}
                        borderRadius={theme.borderRadius.md}
                        backgroundColor={
                          isProceedDisabled || isCreatingOrder
                            ? theme.colors.borderGray
                            : theme.colors.success
                        }
                        onPress={handleProceedToCheckout}
                      />
                    </DemoAttentionPulse>
                  </View>
                )
              }>
              <View style={styles.sheetContentContainer}>
                <Typography style={styles.sheetTitle}>
                  Select Payment Method
                </Typography>
                <View style={styles.paymentOptionRow}>
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={handleSelectCash}
                    style={[
                      styles.paymentOptionCard,
                      selectedPaymentMethod === 'cash' &&
                        styles.paymentOptionCardActive,
                    ]}>
                    <Image
                      source={require('../../assets/cash.png')}
                      style={styles.paymentOptionIcon}
                    />
                    <Typography style={styles.paymentOptionLabel}>
                      Cash
                    </Typography>
                  </TouchableOpacity>
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={handleSelectMpesa}
                    style={[
                      styles.paymentOptionCard,
                      selectedPaymentMethod === 'mpesa' &&
                        styles.paymentOptionCardActive,
                    ]}>
                    <Image
                      source={require('../../assets/mpesa.png')}
                      style={styles.paymentOptionIcon}
                    />
                    <Typography style={styles.paymentOptionLabel}>
                      Mpesa
                    </Typography>
                  </TouchableOpacity>
                </View>

                {selectedPaymentMethod === 'cash' ? (
                  <>
                    <Typography style={styles.sheetSectionTitle}>
                      Amount received
                    </Typography>
                    <BottomSheetTextInput
                      style={styles.sheetTextInput}
                      placeholder="Enter amount"
                      placeholderTextColor={theme.colors.textSecondary}
                      keyboardType="decimal-pad"
                      value={formatNumericStringWithCommas(cashTendered)}
                      onChangeText={handleCashAmountChange}
                    />
                  </>
                ) : (
                  <>
                    <Typography style={styles.sheetSectionTitle}>
                      Mpesa options
                    </Typography>
                    <View style={styles.sheetTabs}>
                      <TouchableOpacity
                        activeOpacity={0.8}
                        onPress={() => handleMpesaModeChange('PAID_TO_TILL')}
                        style={[
                          styles.sheetTab,
                          mpesaSheetMode === 'PAID_TO_TILL' &&
                            styles.sheetTabActive,
                        ]}>
                        <Typography
                          style={[
                            styles.sheetTabText,
                            mpesaSheetMode === 'PAID_TO_TILL' &&
                              styles.sheetTabTextActive,
                          ]}>
                          Paid to Till
                        </Typography>
                      </TouchableOpacity>
                      <TouchableOpacity
                        activeOpacity={0.8}
                        onPress={() => handleMpesaModeChange('STK')}
                        style={[
                          styles.sheetTab,
                          mpesaSheetMode === 'STK' && styles.sheetTabActive,
                        ]}>
                        <Typography
                          style={[
                            styles.sheetTabText,
                            mpesaSheetMode === 'STK' &&
                              styles.sheetTabTextActive,
                          ]}>
                          Send STK Prompt
                        </Typography>
                      </TouchableOpacity>
                    </View>

                    {mpesaSheetMode === 'PAID_TO_TILL' ? (
                      <>
                        <Typography style={styles.sheetSectionTitle}>
                          Amount received
                        </Typography>
                        <BottomSheetTextInput
                          style={styles.sheetTextInput}
                          placeholder="Enter amount"
                          placeholderTextColor={theme.colors.textSecondary}
                          keyboardType="decimal-pad"
                          value={formatNumericStringWithCommas(cashTendered)}
                          onChangeText={handleManualMpesaAmountChange}
                        />
                      </>
                    ) : (
                      <View style={styles.sheetStkContainer}>
                        <View style={styles.phoneInputOuter}>
                          <PhoneInput
                            ref={phoneInputRef}
                            defaultCountry={countryCode as any}
                            value={phoneInputValue}
                            onPress={() =>
                              checkoutPaymentSheetRef.current?.expand()
                            }
                            modalType="bottomSheet"
                            placeholder="720 222 222"
                            minBottomsheetHeight="50%"
                            initialBottomsheetHeight="80%"
                            maxBottomsheetHeight="92%"
                            popularCountries={PREFERRED_COUNTRIES}
                            modalPopularCountriesTitle="Popular countries"
                            modalAllCountriesTitle="All countries"
                            onChangePhoneNumber={handleMpesaPhoneChange}
                            country={phoneSelectedCountry}
                            onChangeCountry={handleMpesaCountryChange}
                            phoneInputStyles={{
                              container: {
                                backgroundColor: 'transparent',
                                height: '100%',
                                alignItems: 'center',
                              },
                              flagContainer: {
                                paddingHorizontal: s(8),
                              },
                              divider: {
                                width: s(Platform.OS === 'web' ? 0.5 : 1),
                              },
                              input: {
                                fontFamily: GTFonts.semibold,
                                fontSize: fs(16),
                                lineHeight: fs(18),
                                ...(Platform.OS === 'web' && {
                                  outlineStyle: 'none',
                                }),
                              } as any,
                              callingCode: {
                                fontFamily: GTFonts.semibold,
                                fontSize: fs(16),
                                lineHeight: fs(18),
                              },
                            }}
                          />
                        </View>
                        {!safaricomNumberValidation(normalizedSheetPhone) &&
                        mpesaPhoneInput.length > 0 ? (
                          <Typography style={styles.sheetValidationText}>
                            Enter a valid Safaricom number
                          </Typography>
                        ) : null}
                        <Button
                          text={`Send STK Push - ${currencyTextCapital} ${formatWithCommas(cartTotal)}`}
                          disabled={
                            isCreatingOrder ||
                            !safaricomNumberValidation(normalizedSheetPhone)
                          }
                          loading={isCreatingOrder}
                          height={v(48)}
                          borderRadius={theme.borderRadius.md}
                          backgroundColor={
                            safaricomNumberValidation(normalizedSheetPhone)
                              ? theme.colors.success
                              : theme.colors.borderGray
                          }
                          onPress={handleCartMpesaStkPush}
                        />
                      </View>
                    )}
                  </>
                )}
              </View>
            </CustomBottomSheet>

            <KeyboardStickyView offset={{ closed: 0, opened: 80 }}>
              <View style={styles.stickyButtonContainer}>
                <View
                  style={[
                    styles.bottom,
                    {
                      paddingHorizontal: s(20),
                      marginBottom: v(15),
                    },
                  ]}>
                  <Typography style={styles.Total}>Total:</Typography>
                  <Typography style={styles.Total}>
                    {currencyTextCapital} {formatWithCommas(cartTotal)}
                  </Typography>
                </View>
                <View style={styles.Buttongrp}>
                  <Button
                    text="Cancel"
                    fontSize={fs(isWeb ? 14 : 12)}
                    backgroundColor={theme.colors.error}
                    rippleColor={theme.colors.errorDark}
                    height={v(isWeb ? 44 : 36)}
                    paddingHorizontal={s(isWeb ? 36 : 24)}
                    borderRadius={theme.borderRadius.md}
                    onPress={handleCancel}
                  />
                  <Button
                    text="Pay Later"
                    fontSize={fs(isWeb ? 14 : 12)}
                    disabled={cartTotal <= 0}
                    backgroundColor={theme.colors.primary}
                    rippleColor={theme.colors.primaryDark}
                    paddingHorizontal={s(isWeb ? 36 : 24)}
                    borderRadius={theme.borderRadius.md}
                    height={v(isWeb ? 44 : 36)}
                    onPress={handlePayLater}
                  />
                  <DemoAttentionPulse active={cartTotal > 0}>
                    <Button
                      text="Checkout"
                      fontSize={fs(isWeb ? 14 : 12)}
                      disabled={cartTotal <= 0}
                      backgroundColor={theme.colors.success}
                      paddingHorizontal={s(isWeb ? 36 : 24)}
                      borderRadius={theme.borderRadius.md}
                      rippleColor={theme.colors.successDark}
                      height={v(isWeb ? 44 : 36)}
                      onPress={handleCheckoutButtonPress}
                    />
                  </DemoAttentionPulse>
                </View>
              </View>
            </KeyboardStickyView>
          </>
        ) : (
          <View style={styles.emptyState}>
            <AnimatedLottieView
              source={require('../../assets/lottie/emptycart.json')}
              autoPlay
              loop
              style={styles.emptyLottie}
            />
            <Typography style={styles.emptyText}>
              There are no items in the basket
            </Typography>
          </View>
        )}
      </View>
      {visibleName && (
        <NameModal SendOrder={handleDemoPayLaterSave} isloading={false} />
      )}
    </SafeAreaWrapper>
  );
};

export default DemoCartScreen;

const createStyles = (theme: any, constrainWidth: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      ...(constrainWidth && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        marginHorizontal: 0,
        paddingHorizontal: s(24),
        backgroundColor: theme.colors.surface,
      }),
    },
    loadingState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cartContainer: {
      flex: 1,
      paddingHorizontal: s(6),
      paddingTop: v(6),
    },
    listHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: s(6),
      marginBottom: v(6),
    },
    listHeaderText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    box: {
      padding: s(4),
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0.5, height: 0.5 },
      shadowRadius: v(1),
      shadowOpacity: 0.35,
      elevation: 2,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.surface,
      marginBottom: v(10),
      borderWidth: borderWidth(0.25),
      borderColor: theme.colors.border,
    },
    cardheader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    leftSection: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    itemImage: {
      width: s(50),
      height: v(50),
      borderRadius: theme.borderRadius.sm,
      marginRight: s(8),
      resizeMode: 'cover',
      backgroundColor: theme.colors.surfaceSecondary,
    },
    emptyImagePlaceholder: {
      width: s(50),
      height: v(50),
      borderRadius: theme.borderRadius.sm,
      borderStyle: 'dashed',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: s(8),
      backgroundColor: theme.colors.surfaceSecondary,
    },
    textColumn: {
      flex: 1,
      gap: v(8),
      minWidth: 0,
    },
    nameText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(17),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    categoryText: {
      fontFamily: LatoFonts.base,
      fontSize: fs(13),
      color: theme.colors.textSecondary,
      marginTop: v(2),
    },
    rightSection: {
      alignItems: 'flex-end',
      marginLeft: s(8),
    },
    qty: {
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    priceRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
    },
    editAmountButton: {
      padding: s(3),
    },
    trashButton: {
      paddingVertical: s(8),
      paddingHorizontal: s(6),
      marginTop: v(2),
    },
    bottom: {
      marginTop: v(6),
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    subTotal: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
      flexShrink: 1,
      marginRight: s(8),
    },
    subTotalPrefix: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    btnQuantity: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(10),
      ...(constrainWidth && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
      }),
    },
    Total: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    Buttongrp: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(8),
      paddingHorizontal: s(6),
      marginBottom: v(6),
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    emptyLottie: {
      width: s(300),
      height: v(300),
      marginBottom: v(20),
    },
    emptyText: {
      fontSize: fs(18),
      fontFamily: LatoFonts.regular,
      color: theme.colors.text,
    },
    sheetContentContainer: {
      paddingHorizontal: s(12),
      paddingVertical: s(12),
      gap: v(12),
    },
    sheetTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: v(4),
    },
    paymentOptionRow: {
      flexDirection: 'row',
      gap: s(8),
      marginBottom: v(24),
    },
    paymentOptionCard: {
      flex: 1,
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      paddingVertical: s(12),
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
      elevation: 2,
    },
    paymentOptionCardActive: {
      borderColor: theme.colors.success,
      backgroundColor:
        (theme.colors as any).successLight ?? 'rgba(76, 175, 80, 0.12)',
    },
    paymentOptionIcon: {
      width: s(70),
      height: v(34),
      marginBottom: v(6),
      resizeMode: 'contain',
    },
    paymentOptionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    sheetSectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    sheetTextInput: {
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      height: v(50),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(12),
      paddingVertical: s(10),
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      color: theme.colors.text,
      backgroundColor: theme.colors.surface,
    },
    sheetTabs: {
      flexDirection: 'row',
      gap: s(8),
      marginBottom: v(12),
    },
    sheetTab: {
      flex: 1,
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      paddingVertical: s(10),
      alignItems: 'center',
    },
    sheetTabActive: {
      borderColor: theme.colors.success,
      backgroundColor:
        (theme.colors as any).successLight ?? 'rgba(76, 175, 80, 0.12)',
    },
    sheetTabText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      color: theme.colors.text,
    },
    sheetTabTextActive: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.success,
    },
    phoneInputOuter: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      marginBottom: v(6),
      borderRadius: s(10),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.borderGray,
      flexDirection: 'row',
      alignItems: 'center',
    },
    sheetValidationText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      color: theme.colors.error,
      marginTop: v(4),
    },
    sheetStkContainer: {
      gap: v(24),
    },
    sheetFooter: {
      paddingHorizontal: s(12),
      backgroundColor: theme.colors.surface,
    },
  });
